import { FC, memo, useCallback } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import {
  GLOBAL_PRESENCE_OPTIONS,
  GlobalPresenceOption,
} from "@/components/filters/UserFilters/GlobalPresenceFilter/constants";
import { LocationSelector } from "@/components/filters/UserFilters/GlobalPresenceFilter/LocationSelector";
import { useCompanyFilterSlice, useCompanyFilterActions } from "../../hooks";

const TESTID = {
  none: "global-presence-option-none-btn",
  multi: "global-presence-option-multinational-btn",
  location: "global-presence-option-location-btn",
  addHq: "global-presence-add-hq-btn",
  addSub: "global-presence-add-subsidiary-btn",
} as const;

function optionTestId(value: GlobalPresenceOption): string {
  if (value === GlobalPresenceOption.MULTI_NATIONAL) return TESTID.multi;
  if (value === GlobalPresenceOption.BY_LOCATION) return TESTID.location;
  return TESTID.none;
}

export const GlobalPresenceFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice("globalPresence");
  const { setSlice } = useCompanyFilterActions();

  const patch = useCallback(
    (next: Partial<typeof slice>) =>
      setSlice("globalPresence", (prev) => ({ ...prev, ...next })),
    [setSlice]
  );

  const handleOptionChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const option = event.target.value as GlobalPresenceOption;
      if (option === GlobalPresenceOption.BY_LOCATION) {
        patch({ option });
      } else {
        patch({
          option,
          showHq: false,
          showSub: false,
          hqValues: [],
          subValues: [],
        });
      }
    },
    [patch]
  );

  const showNote =
    slice.option === GlobalPresenceOption.MULTI_NATIONAL ||
    slice.option === GlobalPresenceOption.BY_LOCATION;

  return (
    <Box id="global-presence-filter">
      <RadioGroup value={slice.option} onChange={handleOptionChange}>
        {GLOBAL_PRESENCE_OPTIONS.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={
              <Radio size="small" data-testid={optionTestId(option.value)} />
            }
            label={option.label}
          />
        ))}
      </RadioGroup>

      {slice.option === GlobalPresenceOption.BY_LOCATION && (
        <Box width="100%">
          <Stack direction="column" spacing={1} mt={1}>
            {!slice.showHq ? (
              <Button
                variant="outlined"
                fullWidth
                data-testid={TESTID.addHq}
                onClick={() => patch({ showHq: true })}
              >
                + ADD HEADQUARTER LOCATIONS
              </Button>
            ) : (
              <LocationSelector
                title="Headquarters"
                initialLocationType={slice.hqType}
                initialLocationValues={slice.hqValues}
                onClose={() => patch({ showHq: false, hqValues: [] })}
                onLocationTypeChange={(hqType) =>
                  patch({ hqType, hqValues: [] })
                }
                onLocationValuesChange={(hqValues) => patch({ hqValues })}
              />
            )}

            {!slice.showSub ? (
              <Button
                variant="outlined"
                fullWidth
                data-testid={TESTID.addSub}
                onClick={() => patch({ showSub: true })}
              >
                + ADD SUBSIDIARY LOCATIONS
              </Button>
            ) : (
              <LocationSelector
                title="Subsidiaries"
                showMatchSelector
                initialLocationType={slice.subType}
                initialLocationValues={slice.subValues}
                initialMatchType={slice.subMatch}
                onClose={() => patch({ showSub: false, subValues: [] })}
                onLocationTypeChange={(subType) =>
                  patch({ subType, subValues: [] })
                }
                onLocationValuesChange={(subValues) => patch({ subValues })}
                onMatchTypeChange={(subMatch) => patch({ subMatch })}
              />
            )}
          </Stack>
        </Box>
      )}

      {showNote && (
        <Box mt={2}>
          <Alert severity="info" sx={{ alignItems: "center" }}>
            Note: The results will contain the entire hierarchy of a company
            that matches the selected criteria
          </Alert>
        </Box>
      )}
    </Box>
  );
});

GlobalPresenceFilter.displayName = "GlobalPresenceFilter";
